import { useId } from "react";
import { TRANSFER_CHANNELS, VAT_SCHEMES } from "@ondo/shared";

import { useModalDialog } from "../lib/useModalDialog.ts";
import { SELL_COPY } from "./sellWizard.constants.ts";
import { SELL_STEPS, artistSharePct } from "./sellWizard.model.ts";
import type { SellDraft } from "./sellWizard.model.ts";
import type {
  BuyerCheck,
  SellWizardController,
} from "./useSellWizardController.ts";
import "../lib/dialog.css";
import "./gallery.css";
import "./sellWizard.css";

/**
 * Selling a consigned work: the five steps, in one dialog.
 *
 * A native `<dialog>` opened with `showModal()` — the choice `AddHistoryDialog`
 * and `WorkPickerDialog` both make, and for the same list of things it does that
 * would otherwise have to be written and got right: modal semantics, Escape, a
 * real focus trap, the rest of the document made INERT rather than merely
 * covered, focus returned to whatever opened it, and the top layer, so there is
 * no portal and no z-index scale to invent.
 *
 * MOUNTED EVEN WHEN SHUT, deliberately. Closing by unmounting would skip
 * `close()`, and it is `close()` that returns focus to the Sell button.
 *
 * The view holds no business condition: which step is reachable, what the fee
 * is, what Convert will actually do and whether the buyer is on ONDO are all
 * decided in `sellWizard.model.ts` and arrive on the controller's view-model.
 */
export function SellWizard({ controller }: { controller: SellWizardController }) {
  const { view, setField, next, back, submit, close, checkOndoId, confirmBuyer } =
    controller;
  const dialog = useModalDialog(view.open);
  const titleId = useId();
  const base = useId();

  return (
    <dialog
      ref={dialog}
      className="sell"
      aria-labelledby={titleId}
      // Escape and the backdrop both close natively; this is what tells React it
      // happened. Without it the two disagree and the next render calls
      // showModal() on a dialog that is already open.
      onClose={close}
    >
      <div className="sell__head">
        <div>
          <h2 id={titleId} className="sell__title">
            {SELL_COPY.title}
          </h2>
          {view.work && (
            <p className="sell__subject">
              {view.work.title} · {view.work.attributedName} · {view.work.ondoCode}
            </p>
          )}
        </div>
        <button
          type="button"
          className="sell__close ondo-dialog__close"
          aria-label={SELL_COPY.close}
          onClick={close}
        >
          ✕
        </button>
      </div>

      {view.open && (
        <>
          <ol className="sell__steps">
            {SELL_STEPS.map((step, index) => {
              const state = view.done.includes(step)
                ? "done"
                : step === view.step
                  ? "on"
                  : "ahead";

              return (
                <li key={step} className={`sell__step sell__step--${state}`}>
                  <span className="sell__step-mark" aria-hidden="true">
                    {state === "done" ? "✓" : index + 1}
                  </span>
                  <span className="sell__step-label">{SELL_COPY.steps[step]}</span>
                </li>
              );
            })}
          </ol>

          <div className="sell__body">
            {view.step === "consignment" && (
              <>
                <p className="sell__lead">{SELL_COPY.consignmentLead}</p>

                <dl className="sell__facts">
                  {view.facts.map((fact) => (
                    <div key={fact.label} className="sell__fact">
                      <dt>{fact.label}</dt>
                      <dd>{fact.value}</dd>
                    </div>
                  ))}
                </dl>

                <p className="sell__note">{SELL_COPY.noDocument}</p>
                <p className="sell__prose">{SELL_COPY.consignmentContinue}</p>
              </>
            )}

            {view.step === "buyer" && (
              <BuyerStep
                base={base}
                draft={view.draft}
                setField={setField}
                check={view.check}
                onCheck={checkOndoId}
                onConfirm={confirmBuyer}
              />
            )}

            {view.step === "covenant" && (
              <CovenantStep base={base} draft={view.draft} setField={setField} />
            )}

            {view.step === "fee" && (
              <section className="sell__fee">
                <h3 className="sell__legend">
                  {SELL_COPY.feeLegend(view.fee.kind === "premium")}
                </h3>
                <p className="sell__fee-figure">
                  {view.fee.amount} <span>{SELL_COPY.feeCurrency}</span>
                </p>
                <p className="sell__fee-chips">
                  <span
                    className={`sell__fee-chip${view.fee.kind === "plain" ? " sell__fee-chip--on" : ""}`}
                  >
                    {SELL_COPY.feePlainChip}
                  </span>
                  <span
                    className={`sell__fee-chip${view.fee.kind === "premium" ? " sell__fee-chip--on" : ""}`}
                  >
                    {SELL_COPY.feePremiumChip}
                  </span>
                </p>
                <p className="sell__note">{SELL_COPY.feeNote}</p>
              </section>
            )}

            {view.step === "convert" && (
              <>
                <dl className="sell__facts sell__facts--summary">
                  {view.summary.map((row) => (
                    <div key={row.label} className="sell__fact">
                      <dt>{row.label}</dt>
                      <dd>{row.value}</dd>
                    </div>
                  ))}
                </dl>

                <p className="sell__effect">{view.effect}</p>

                <label className="sell__confirm" htmlFor={`${base}-confirm`}>
                  <input
                    id={`${base}-confirm`}
                    type="checkbox"
                    checked={view.draft.confirmedTitle}
                    onChange={(event) =>
                      setField("confirmedTitle", event.currentTarget.checked)
                    }
                  />
                  <span>{SELL_COPY.confirmTitle}</span>
                </label>
              </>
            )}

            {view.error && (
              <p className="sell__error" role="alert">
                {view.error}
              </p>
            )}
          </div>

          <div className="sell__actions">
            <button
              type="button"
              className="sell__back"
              onClick={back}
              disabled={view.step === "consignment" || view.submitting}
            >
              {SELL_COPY.back}
            </button>

            {view.step === "convert" ? (
              <button
                type="button"
                className="sell__submit"
                onClick={submit}
                disabled={!view.canAdvance}
              >
                {view.submitting ? SELL_COPY.submitting : SELL_COPY.submit}
              </button>
            ) : (
              <button
                type="button"
                className="sell__next"
                onClick={next}
                disabled={!view.canAdvance}
              >
                {SELL_COPY.next}
              </button>
            )}
          </div>
        </>
      )}
    </dialog>
  );
}

type SetField = <K extends keyof SellDraft>(key: K, value: SellDraft[K]) => void;

/**
 * Who is buying, and for how much.
 *
 * The two-way toggle decides which field is READ as well as which is shown, so a
 * name left behind by switching cannot be submitted beside an id — see
 * `namedBuyer`. Neither side searches for anybody.
 */
function BuyerStep({
  base,
  draft,
  setField,
  check,
  onCheck,
  onConfirm,
}: {
  base: string;
  draft: SellDraft;
  setField: SetField;
  check: BuyerCheck | null;
  onCheck: () => void;
  onConfirm: (confirmed: boolean) => void;
}) {
  return (
    <section className="sell__section">
      <h3 className="sell__legend">{SELL_COPY.buyerLegend}</h3>

      <div className="sell__toggle" role="group" aria-label={SELL_COPY.buyerLegend}>
        <button
          type="button"
          className={`sell__toggle-half${draft.buyerMode === "ondo" ? " sell__toggle-half--on" : ""}`}
          aria-pressed={draft.buyerMode === "ondo"}
          onClick={() => setField("buyerMode", "ondo")}
        >
          {SELL_COPY.matchOnNetwork}
        </button>
        <button
          type="button"
          className={`sell__toggle-half${draft.buyerMode === "name" ? " sell__toggle-half--on" : ""}`}
          aria-pressed={draft.buyerMode === "name"}
          onClick={() => setField("buyerMode", "name")}
        >
          {SELL_COPY.createNewOwner}
        </button>
      </div>

      {draft.buyerMode === "ondo" ? (
        <>
          <label className="sell__label" htmlFor={`${base}-ondo`}>
            {SELL_COPY.buyerOndoLabel}
          </label>
          <input
            id={`${base}-ondo`}
            className="sell__input"
            value={draft.buyerOndoId}
            placeholder={SELL_COPY.buyerOndoPlaceholder}
            autoComplete="off"
            spellCheck={false}
            // ON BLUR, never per keystroke: every refusal spends one of ten
            // probes an hour, and a field checked as it is typed would burn the
            // budget on the prefixes of a correct id.
            onBlur={onCheck}
            onChange={(event) => setField("buyerOndoId", event.currentTarget.value)}
          />
          <p className="sell__help">{SELL_COPY.matchHelp}</p>

          {check?.state === "checking" && (
            <p className="sell__note" role="status">
              {SELL_COPY.checking}
            </p>
          )}

          {/* The server's own sentence — the SAME one the sale raises, so
              pressing on does not produce a second explanation of one refusal.
              It carries no name, because a refused check must disclose exactly
              what a refused sale does. */}
          {check?.state === "refused" && (
            <p className="sell__error" role="alert">
              {check.message}
            </p>
          )}

          {/* THE NAME, and the tick that is the actual gate. Never pre-ticked:
              a box already agreed to is not a confirmation. */}
          {check?.state === "found" && (
            <section className="sell__found">
              <p className="sell__found-legend">{SELL_COPY.foundLegend}</p>
              <p className="sell__found-name">{check.displayName}</p>
              <p className="sell__found-id">{check.ondoId}</p>

              <label className="sell__check" htmlFor={`${base}-confirm-buyer`}>
                <input
                  id={`${base}-confirm-buyer`}
                  type="checkbox"
                  checked={check.confirmed}
                  onChange={(event) => onConfirm(event.currentTarget.checked)}
                />
                <span>{SELL_COPY.confirmBuyer}</span>
              </label>
              <p className="sell__help">{SELL_COPY.confirmHelp}</p>
            </section>
          )}
        </>
      ) : (
        <>
          <label className="sell__label" htmlFor={`${base}-name`}>
            {SELL_COPY.buyerNameLabel}
          </label>
          <input
            id={`${base}-name`}
            className="sell__input"
            value={draft.buyerName}
            placeholder={SELL_COPY.buyerNamePlaceholder}
            onChange={(event) => setField("buyerName", event.currentTarget.value)}
          />
          <p className="sell__help">{SELL_COPY.createHelp}</p>
        </>
      )}

      <div className="sell__pair">
        <div>
          <label className="sell__label" htmlFor={`${base}-price`}>
            {SELL_COPY.priceLabel}
          </label>
          <input
            id={`${base}-price`}
            className="sell__input"
            inputMode="decimal"
            value={draft.price}
            onChange={(event) => setField("price", event.currentTarget.value)}
          />
        </div>
        <div>
          <label className="sell__label" htmlFor={`${base}-currency`}>
            {SELL_COPY.currencyLabel}
          </label>
          <input
            id={`${base}-currency`}
            className="sell__input sell__input--short"
            maxLength={3}
            value={draft.currency}
            onChange={(event) => setField("currency", event.currentTarget.value)}
          />
        </div>
      </div>
      <p className="sell__help">{SELL_COPY.priceHelp}</p>

      <label className="sell__label" htmlFor={`${base}-channel`}>
        {SELL_COPY.channelLabel}
      </label>
      <select
        id={`${base}-channel`}
        className="sell__input"
        value={draft.channel}
        onChange={(event) =>
          setField("channel", event.currentTarget.value as SellDraft["channel"])
        }
      >
        {TRANSFER_CHANNELS.map((channel) => (
          <option key={channel} value={channel}>
            {SELL_COPY.channel[channel]}
          </option>
        ))}
      </select>

      <label className="sell__check" htmlFor={`${base}-vat`}>
        <input
          id={`${base}-vat`}
          type="checkbox"
          checked={draft.addVat}
          onChange={(event) => setField("addVat", event.currentTarget.checked)}
        />
        <span>
          <strong>{SELL_COPY.addVat}</strong> — {SELL_COPY.addVatHelp}
        </span>
      </label>

      {draft.addVat && (
        <>
          <div className="sell__pair">
            <div>
              <label className="sell__label" htmlFor={`${base}-scheme`}>
                {SELL_COPY.vatSchemeLabel}
              </label>
              <select
                id={`${base}-scheme`}
                className="sell__input"
                value={draft.vatScheme}
                onChange={(event) =>
                  setField("vatScheme", event.currentTarget.value as SellDraft["vatScheme"])
                }
              >
                {VAT_SCHEMES.map((scheme) => (
                  <option key={scheme} value={scheme}>
                    {SELL_COPY.vatScheme[scheme]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="sell__label" htmlFor={`${base}-vat-amount`}>
                {SELL_COPY.vatAmountLabel}
              </label>
              <input
                id={`${base}-vat-amount`}
                className="sell__input"
                inputMode="decimal"
                value={draft.vat}
                onChange={(event) => setField("vat", event.currentTarget.value)}
              />
            </div>
          </div>
          <p className="sell__help">{SELL_COPY.vatHelp}</p>
        </>
      )}

      <p className="sell__note">{SELL_COPY.auctionNote}</p>
    </section>
  );
}

/**
 * The covenant, or none.
 *
 * Gated by a checkbox rather than inferred from the sliders, because "I did not
 * fill this in" and "there is no covenant" are different answers and only one of
 * them is a term. The split is ONE slider: the artist's share is the remainder,
 * so the two halves cannot be set to something the database will refuse.
 */
function CovenantStep({
  base,
  draft,
  setField,
}: {
  base: string;
  draft: SellDraft;
  setField: SetField;
}) {
  return (
    <section className="sell__section">
      <label className="sell__check sell__check--lead" htmlFor={`${base}-covenant`}>
        <input
          id={`${base}-covenant`}
          type="checkbox"
          checked={draft.covenantOn}
          onChange={(event) => setField("covenantOn", event.currentTarget.checked)}
        />
        <span>
          <strong>{SELL_COPY.covenantToggle}</strong>
          <br />
          {SELL_COPY.covenantHelp}
        </span>
      </label>

      {draft.covenantOn && (
        <div className="sell__terms">
          <label className="sell__slider-label" htmlFor={`${base}-term`}>
            {SELL_COPY.termLabel(draft.termYears)}
          </label>
          <input
            id={`${base}-term`}
            className="sell__slider"
            type="range"
            min={1}
            max={SELL_COPY.termMax}
            value={draft.termYears}
            onChange={(event) => setField("termYears", Number(event.currentTarget.value))}
          />

          <label className="sell__slider-label" htmlFor={`${base}-share`}>
            {SELL_COPY.shareLabel(draft.resaleSharePct)}
          </label>
          <input
            id={`${base}-share`}
            className="sell__slider"
            type="range"
            min={0}
            max={100}
            value={draft.resaleSharePct}
            onChange={(event) =>
              setField("resaleSharePct", Number(event.currentTarget.value))
            }
          />
          <p className="sell__help">{SELL_COPY.shareHelp}</p>

          <label className="sell__slider-label" htmlFor={`${base}-split`}>
            {SELL_COPY.splitLabel(draft.gallerySharePct, artistSharePct(draft))}
          </label>
          <input
            id={`${base}-split`}
            className="sell__slider"
            type="range"
            min={0}
            max={100}
            value={draft.gallerySharePct}
            onChange={(event) =>
              setField("gallerySharePct", Number(event.currentTarget.value))
            }
          />

          <label className="sell__check" htmlFor={`${base}-elected`}>
            <input
              id={`${base}-elected`}
              type="checkbox"
              checked={draft.artistElected}
              onChange={(event) => setField("artistElected", event.currentTarget.checked)}
            />
            <span>{SELL_COPY.artistElected}</span>
          </label>

          <label className="sell__check" htmlFor={`${base}-locks`}>
            <input
              id={`${base}-locks`}
              type="checkbox"
              checked={draft.locksTransfer}
              onChange={(event) => setField("locksTransfer", event.currentTarget.checked)}
            />
            <span>{SELL_COPY.locksTransfer}</span>
          </label>

          {draft.locksTransfer && <p className="sell__note">{SELL_COPY.locksNote}</p>}
        </div>
      )}
    </section>
  );
}
